import { InlineIcon } from "@iconify/react";
import { t } from "i18next";
import _ from "lodash";
import PropTypes from "prop-types";
import React, { useContext, useEffect } from "react";
import { createUseStyles } from "react-jss";
import { useSelector } from "react-redux";
import { useHistory, withRouter } from "react-router-dom";
import { Button } from "../../components/common/Button";
import { sendPixelEvent } from "../../helpers";
import jssVariables from "../../jss_setup";
import { StyleTypeContext } from "../../umbilical_tools";
import { NoScrollContext } from "../../context";
import { toast } from "react-toastify";

const LOGO = import.meta.env.VITE_APP_LOGO;

const ButtonDefaultStyles = {
  ButtonDefault: {
    background: "#B2B2B2",
    border: "1px solid #B2B2B2",
    borderRadius: jssVariables.half_gap,
    "&:hover": {
      color: "white",
      background: "#B2B2B2",
    },
    smallscreen: {
      background: "#B2B2B2",
      border: "1px solid #B2B2B2",
      "&:hover": {
        color: "#B2B2B2",
        background: "white",
      },
    },
  },
};
const PRODUCT_MSG_MAP = {
  hotel: [
    "It appears that we are unable to fulfil your reservation request due to a lack of availability.",
    "Please consider modifying your travel dates or checking out our other properties in the area.",
    "We will be more than happy to reach out to the hotel on your behalf to try and make your dream holiday a reality.",
    "We are always here to help!",
  ].join(" "),
  activity:
    "Ooops! It seems we coulnd't find the activities you were looking for. Please contact us with your request and we will get back to you as soon as possible!",
  flight:
    "Ooops! It seems we coulnd't find the flights you were looking for. Please contact us with your request and we will get back to you as soon as possible!",
  transfer:
    "Ooops! It seems we coulnd't find the transfers you were looking for. Please contact us with your request and we will get back to you as soon as possible!",
  restaurant:
    "Ooops! It seems we coulnd't find the restaurants you were looking for. Please contact us with your request and we will get back to you as soon as possible!",
};

const contactBtnsStyles = createUseStyles({
  ContactBtns: (props) => ({
    display: "grid",
    gridTemplateColumns: `repeat(${props.btnColumnsNo}, max-content)`,
    gridGap: jssVariables.half_gap,
    zIndex: 9999,
    ..._.get(props, "customStyles.contactBtns", {}),
  }),
  contactBtn: (props) => ({
    display: "grid",
    gridTemplateColumns: "max-content max-content",
    gridGap: jssVariables.half_gap,
    alignItems: "center",
    justifyContent: "center",
    border: `1px solid ${jssVariables.colors.base_color}`,
    borderRadius: jssVariables.half_gap,
    padding: `calc(${jssVariables.normal_gap} * .3) calc(${jssVariables.normal_gap} * 1)`,
    cursor: "pointer",
    background: jssVariables.colors.base_color,
    color: jssVariables.colors.text.light,
    fontWeight: "bold",
    width: "7rem",
    textAlign: "center",
    fontSize: "small",
    textDecoration: "none",
    "&:hover": {
      background: "white",
      color: jssVariables.colors.base_color,
      textDecoration: "none",
    },
    ..._.get(props, "customStyles.contactBtn", {}),
  }),
});
export const ContactBtns = ({ email, product_type, phone, whatsapp }) => {
  const { type, brand, cStyler } = useContext(StyleTypeContext);
  const customStyles = cStyler({ type, brand, objName: "SearchFeedback" });

  const btnColumnsNo = [email, product_type, phone, whatsapp]
    .map((attr) => Boolean(attr))
    .filter((attr) => attr).length;

  const classes = contactBtnsStyles({ customStyles, btnColumnsNo });

  return (
    <div className={classes.ContactBtns}>
      {email && (
        <a
          href={`mailto:${email}`}
          className={classes.contactBtn}
          onClick={(e) => {
            sendPixelEvent("SearchFeedbackContact", {
              data: { product_type },
              withPageView: false,
            });
          }}>
          <InlineIcon icon="ic:baseline-email" className={classes.btnIcon} />
          <strong>{t("email")}</strong>
        </a>
      )}
      {phone && (
        <a
          href={`tel:${phone}`}
          className={classes.contactBtn}
          onClick={(e) => {
            sendPixelEvent("SearchFeedbackContact", {
              data: { product_type },
              withPageView: false,
            });
          }}>
          <InlineIcon
            icon="ic:baseline-local-phone"
            className={classes.btnIcon}
          />
          <strong>Phone</strong>
        </a>
      )}
      {whatsapp && (
        <a
          href={`tel:${whatsapp}`}
          className={classes.contactBtn}
          onClick={(e) => {
            sendPixelEvent("SearchFeedbackContact", {
              data: { product_type },
              withPageView: false,
            });
          }}>
          <InlineIcon icon="ic:baseline-whatsapp" className={classes.btnIcon} />
          <strong>What's app</strong>
        </a>
      )}
      {brand === "tmdh" && (
        <a
          className={classes.contactBtn}
          onClick={() => {
            toast.info(
              "Please click the Messenger icon on the bottom right corner of the page to initial a chat session.",
              { autoClose: 10000 }
            );
          }}>
          <InlineIcon
            icon="mdi:facebook-messenger"
            className={classes.btnIcon}
          />
          <strong>Messenger</strong>
        </a>
      )}
    </div>
  );
};
ContactBtns.propTypes = {
  email: PropTypes.string,
  product_type: PropTypes.string,
  phone: PropTypes.string,
  whatsapp: PropTypes.string,
};

const styles = createUseStyles({
  SearchFeedback: (props) => ({
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    background: jssVariables.colors.background.light,
    zIndex: 9999,
    display: "grid",
    placeContent: "center",
    placeItems: "center",
    gridGap: jssVariables.double_gap,
    ..._.get(props, "customStyles.SearchFeedback", {}),
  }),
  backgroundImgContr: (props) => ({
    width: "100vw",
    height: "100vh",
    position: "absolute",
    ..._.get(props, "customStyles.backgroundImgContr", {}),
  }),
  lineMobTop: (props) => ({
    position: "absolute",
    top: jssVariables.double_gap,
    right: 0,
    ..._.get(props, "customStyles.lineMobTop", {}),
  }),
  lineMobBtm: (props) => ({
    position: "fixed",
    bottom: `calc(${jssVariables.normal_gap} * 4)`,
    right: 0,
    ..._.get(props, "customStyles.lineMobBtm", {}),
  }),
  lineDeskTop: (props) => ({
    position: "absolute",
    top: "-10vw",
    left: "20vw",
    width: "100vw",
    display: "none",
    ..._.get(props, "customStyles.lineDeskTop", {}),
  }),
  lineDeskBtm: (props) => ({
    position: "absolute",
    bottom: `calc(${jssVariables.normal_gap} * 4)`,
    right: 0,
    width: "100vw",
    display: "none",
    ..._.get(props, "customStyles.lineDeskBtm", {}),
  }),
  logoContr: (props) => ({
    padding: [jssVariables.normal_gap, jssVariables.double_gap],
    ..._.get(props, "customStyles.logoContr", {}),
  }),
  logo: (props) => ({
    maxWidth: "75vw",
    height: `calc(${jssVariables.navbar_height} * 1.5)`,
    objectFit: "contain",
    ..._.get(props, "customStyles.logo", {}),
  }),
  text: (props) => ({
    textAlign: "center",
    fontSize: "large",
    padding: [0, jssVariables.normal_gap],
    ..._.get(props, "customStyles.text", {}),
  }),
  contactBtnsContr: (props) => ({
    display: "grid",
    placeItems: "center",
    gridGap: jssVariables.normal_gap,
    ..._.get(props, "customStyles.contactBtnsContr", {}),
  }),
  contactBtnsContrTitle: (props) => ({
    fontSize: "large",
    fontWeight: "bold",
    color: jssVariables.colors.base_color,
    ..._.get(props, "customStyles.contactBtnsContrTitle", {}),
  }),
  contactBtns: (props) => ({
    display: "grid",
    gridTemplateColumns: "repeat(3, max-content)",
    gridGap: jssVariables.half_gap,
    ..._.get(props, "customStyles.contactBtns", {}),
  }),
  contactBtn: (props) => ({
    display: "grid",
    gridTemplateColumns: "max-content max-content",
    gridGap: jssVariables.half_gap,
    alignItems: "center",
    justifyContent: "center",
    border: `1px solid ${jssVariables.colors.base_color}`,
    borderRadius: jssVariables.half_gap,
    padding: `calc(${jssVariables.normal_gap} * .3) calc(${jssVariables.normal_gap} * 1)`,
    cursor: "pointer",
    background: jssVariables.colors.base_color,
    color: jssVariables.colors.text.light,
    fontWeight: "bold",
    width: "7rem",
    textAlign: "center",
    fontSize: "small",
    textDecoration: "none",
    "&:hover": {
      background: "white",
      color: jssVariables.colors.base_color,
      textDecoration: "none",
    },
    ..._.get(props, "customStyles.contactBtn", {}),
  }),
  backBtnContr: (props) => ({
    display: "grid",
    paddingTop: jssVariables.double_gap,
    zIndex: 9999,
    ..._.get(props, "customStyles.backBtnContr", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    SearchFeedback: (props) => ({
      padding: jssVariables.spacing.center_padding_large,
      ..._.get(props, "customStyles.SearchFeedback.smallscreen", {}),
    }),
    lineMobTop: (props) => ({
      display: "none",
      ..._.get(props, "customStyles.lineMobTop.smallscreen", {}),
    }),
    lineMobBtm: (props) => ({
      display: "none",
      ..._.get(props, "customStyles.lineMobBtm.smallscreen", {}),
    }),
    lineDeskTop: (props) => ({
      display: "block",
      ..._.get(props, "customStyles.lineDeskTop.smallscreen", {}),
    }),
    lineDeskBtm: (props) => ({
      display: "block",
      ..._.get(props, "customStyles.lineDeskBtm.smallscreen", {}),
    }),
    logo: (props) => ({
      maxWidth: "25rem",
      ..._.get(props, "customStyles.logo.smallscreen", {}),
    }),
    contactBtns: (props) => ({
      gridGap: jssVariables.normal_gap,
      ..._.get(props, "customStyles.contactBtns.smallscreen", {}),
    }),
    contactBtn: (props) => ({
      width: "9rem",
      fontSize: "medium",
      ..._.get(props, "customStyles.contactBtn.smallscreen", {}),
    }),
  },
});
const SearchFeedback = ({ product_type }) => {
  const { type, brand, cStyler } = useContext(StyleTypeContext);
  const { toggleNoScroll } = useContext(NoScrollContext);

  const customStyles = cStyler({ type, brand, objName: "SearchFeedback" });
  const classes = styles({ customStyles });
  const history = useHistory();

  const { email, phone, emergency_phone } = useSelector(
    (state) =>
      _.get(state.WebsiteMetadata, "metadata.contact_information", {}) || {}
  );

  useEffect(() => {
    sendPixelEvent("SearchFeedbackPage");
    toggleNoScroll(true);
  }, []);

  useEffect(() => {
    return toggleNoScroll(false);
  }, []);

  return (
    <div className={classes.SearchFeedback}>
      <svg
        className={classes.lineMobTop}
        viewBox="0 0 393 141"
        fill="none"
        xmlns="http://www.w3.org/2000/svg">
        <path
          d="M396 139C393.382 134.869 389.487 129.357 386.315 125.674C360.645 95.8589 325.844 74.9259 288.913 62.6311C244.327 47.7879 195.79 45.9756 149.273 45.2879C116.257 44.7999 82.1143 43.443 50.3348 33.4079C31.5133 27.4645 13.3761 16.4658 -0.100098 1.5"
          strokeWidth="3"
          stroke={jssVariables.colors.base_color}
          strokeLinecap="round"
        />
      </svg>
      <svg
        className={classes.lineMobBtm}
        viewBox="0 0 393 26"
        fill="none"
        xmlns="http://www.w3.org/2000/svg">
        <path
          d="M393 1.84985C319.82 13.8507 246.322 22.3504 172.3 23.8444C118.271 24.9349 59.771 20.2514 0 11.0343"
          strokeWidth="3"
          strokeLinecap="round"
          stroke={jssVariables.colors.base_color}
        />
      </svg>
      <svg
        className={classes.lineDeskTop}
        viewBox="0 0 1117 476"
        fill="none"
        xmlns="http://www.w3.org/2000/svg">
        <path
          d="M2 0C21.2863 95.4296 104.18 164.966 192.739 192.816C277.713 219.539 369.005 223.153 457.286 224.452C581.666 226.283 711.447 231.109 830.664 270.636C929.412 303.376 1022.46 359.119 1091.1 438.513C1099.58 448.322 1110 463 1117 474"
          strokeWidth="3"
          strokeLinecap="round"
          stroke={jssVariables.colors.base_color}
        />
      </svg>
      <svg
        className={classes.lineDeskBtm}
        viewBox="0 0 1440 161"
        fill="none"
        xmlns="http://www.w3.org/2000/svg">
        <path
          d="M0 2C205.398 94.6322 480.546 163.215 699.2 158.798C888.66 154.971 1075.47 115.718 1261.69 76.0222C1344.49 58.3706 1443 23.6154 1443 23.6154"
          strokeWidth="3"
          strokeLinecap="round"
          stroke={jssVariables.colors.base_color}
        />
      </svg>
      <div className={classes.logoContr}>
        <img className={classes.logo} alt="logo" src={LOGO} />
      </div>
      <div className={classes.text}>{PRODUCT_MSG_MAP[product_type]}</div>
      <ContactBtns
        email={email}
        product_type={product_type}
        phone={brand !== "tmdh" ? phone : ""}
        whatsapp={brand === "tmdh" ? emergency_phone : ""}
      />
      <div className={classes.backBtnContr}>
        <Button
          id="backToHome"
          text={t("backToHome")}
          bold={true}
          size="small"
          onClick={() => history.push("/")}
          customStyles={{
            ...ButtonDefaultStyles,
            ...customStyles.backBtn,
          }}
        />
      </div>
    </div>
  );
};
SearchFeedback.propTypes = {
  product_type: PropTypes.string,
  history: PropTypes.object.isRequired,
};

export default withRouter(SearchFeedback);
